/**
 * The map on the stamp preview card (SPEC_STAMP_ROUND §B.2 as built for the capture plane — be #170,
 * `SvCaptureController.stampTile`): the be serves the z16 tile a point snaps to THROUGH its own route; the browser
 * never fetches a tile from anywhere else. Residency rule 8 in one line: the ONLY map URL on this surface is
 * `STAMP_TILE_PATH`, on our own base, with the representative's capture bearer — the no-tile scan allows exactly it.
 *
 *   GET /api/v1/sv/capture/stamp-tile?lat&lng  (Authorization: Bearer <capture bearer>)
 *     200 image/png            the raw 256 px tile, `X-SV-Stamp-Tile: DRAWN`, `X-SV-Stamp-Tile-Key: z/x/y`
 *     204                      `X-SV-Stamp-Tile: OFF | HIDDEN` — a tenant / deploy state, not an error: no map, no note
 *     404 / 410                an older be without the route — same as OFF
 *     503 SV_TILE_UNAVAILABLE  the in-VPC source could not answer   ┐ no map, the note
 *     429 SV_RATE_LIMITED      the per-case bucket                  ┘ "Map not available right now"
 *
 * Two views share one bounded cache (`loadTile`, keyed by tile): the THUMBNAIL (the fix's own tile, the app's pin at
 * the fix's fractional offset × 256, the registered-address marker when it falls in the same tile — refetched only
 * when the fix crosses into another z16 tile) and the EXPANDED sheet (a 3×3 MOSAIC of the eight neighbours around
 * that tile — nine requests, cached; the same route, the neighbours addressed by their tile CENTRES). No pan or zoom
 * in this cut. A request names a tile's centre, not the fix, so a tile is one request however the fix moves inside it.
 * The map is a view; nothing here is bound, chained or sent — the shot never waits for it.
 *
 * NOTE on the gate: the exchange's `summary.stamp` carries NO `mapTile` field (verified against the be's
 * `sv-exchange-stamp.json`; `mapTile` exists only on the console's config view), so the client does not invent one:
 * it asks whenever the card shows coordinates (`liveCard = COORDINATES` on a be ≥ v0.1.12) and lets the be's
 * 204 OFF / HIDDEN decide — the same semantics `/lines` `tile` carries.
 */

import { getBearer } from '@/api/client';
import { API_BASE_URL } from '@/api/transport';

export const TILE_ZOOM = 16;
export const TILE_PX = 256;
/** The one and only map URL this surface fetches — our own be route. */
export const STAMP_TILE_PATH = '/api/v1/sv/capture/stamp-tile';
/** The expanded sheet: one ring of neighbours around the fix's tile (3×3). */
export const MOSAIC_RADIUS = 1;
export const MOSAIC_SIDE = 2 * MOSAIC_RADIUS + 1;
export const MOSAIC_PX = MOSAIC_SIDE * TILE_PX;

export interface TileRef {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

/** A marker's position inside a tile (0 ≤ x, y < 256) — or inside the mosaic (0 ≤ x, y < 768) — in pixels. */
export interface TilePin {
  readonly x: number;
  readonly y: number;
}

const LAT_LIMIT = 85.0511287798;

/** Fractional slippy-map tile coordinates (OSM convention) for a point at zoom `z`. */
export function tileCoords(lat: number, lng: number, z = TILE_ZOOM): { readonly xt: number; readonly yt: number } {
  const n = 2 ** z;
  const latRad = (Math.max(-LAT_LIMIT, Math.min(LAT_LIMIT, lat)) * Math.PI) / 180;
  const xt = ((lng + 180) / 360) * n;
  const yt = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { xt, yt };
}

/** The tile a point snaps to, and where the point sits inside it. */
export function tileFor(lat: number, lng: number, z = TILE_ZOOM): { readonly ref: TileRef; readonly pin: TilePin } {
  const { xt, yt } = tileCoords(lat, lng, z);
  const x = Math.floor(xt);
  const y = Math.floor(yt);
  return { ref: { z, x, y }, pin: { x: (xt - x) * TILE_PX, y: (yt - y) * TILE_PX } };
}

export function tileKey(ref: TileRef): string {
  return `${ref.z}/${ref.x}/${ref.y}`;
}

/** The inverse: the geographic centre of a tile — what a request for that tile names. */
export function tileCentre(ref: TileRef): { readonly lat: number; readonly lng: number } {
  const n = 2 ** ref.z;
  const lng = ((ref.x + 0.5) / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (ref.y + 0.5)) / n)));
  return { lat: (latRad * 180) / Math.PI, lng };
}

/** Where a point sits inside `ref`, or null when it falls outside that tile (then no marker is drawn for it). */
export function pinWithin(ref: TileRef, lat: number, lng: number): TilePin | null {
  const { xt, yt } = tileCoords(lat, lng, ref.z);
  if (Math.floor(xt) !== ref.x || Math.floor(yt) !== ref.y) return null;
  return { x: (xt - ref.x) * TILE_PX, y: (yt - ref.y) * TILE_PX };
}

/** The 3×3 mosaic around `centre`, row-major from the north-west corner; index 4 is `centre` itself. */
export function mosaicRefs(centre: TileRef, radius = MOSAIC_RADIUS): TileRef[] {
  const out: TileRef[] = [];
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) out.push({ z: centre.z, x: centre.x + dx, y: centre.y + dy });
  }
  return out;
}

/** A point's pixel position inside the mosaic around `centre`, or null when it lies outside the mosaic. */
export function pinWithinMosaic(centre: TileRef, lat: number, lng: number, radius = MOSAIC_RADIUS): TilePin | null {
  const { xt, yt } = tileCoords(lat, lng, centre.z);
  const x = (xt - (centre.x - radius)) * TILE_PX;
  const y = (yt - (centre.y - radius)) * TILE_PX;
  const side = (2 * radius + 1) * TILE_PX;
  if (x < 0 || y < 0 || x >= side || y >= side) return null;
  return { x, y };
}

export type EdgeSide = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

/** Which way a point outside the mosaic lies, from the mosaic's centre — for the edge arrow. */
export function edgeSideFor(centre: TileRef, lat: number, lng: number): EdgeSide {
  const { xt, yt } = tileCoords(lat, lng, centre.z);
  const dx = xt - (centre.x + 0.5);
  const dy = yt - (centre.y + 0.5); // tile y grows southward
  const deg = (Math.atan2(dx, -dy) * 180) / Math.PI; // 0 = north, clockwise
  const sides: EdgeSide[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return sides[Math.round((((deg % 360) + 360) % 360) / 45) % 8] as EdgeSide;
}

export type TileAnswer =
  | { readonly kind: 'png'; readonly blob: Blob; readonly state: string; readonly key: string | null }
  /** 204 OFF | HIDDEN, a be without the route (ABSENT), or no bearer yet (NO_BEARER): nothing to draw, nothing to say. */
  | { readonly kind: 'none'; readonly state: string }
  | { readonly kind: 'error'; readonly reason: 'unavailable' | 'rate-limited' | 'network' | 'unexpected' };

export interface TileFetchDeps {
  readonly fetch: typeof fetch;
  readonly bearer: () => string | null;
  readonly base: string;
}

/** One request for the tile under a point. Never throws; never blocks anything. */
export async function fetchStampTile(lat: number, lng: number, deps: Partial<TileFetchDeps> = {}): Promise<TileAnswer> {
  const bearer = (deps.bearer ?? getBearer)();
  if (!bearer) return { kind: 'none', state: 'NO_BEARER' };
  const doFetch = deps.fetch ?? (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return { kind: 'error', reason: 'unexpected' };
  const url = `${deps.base ?? API_BASE_URL}${STAMP_TILE_PATH}?lat=${encodeURIComponent(String(lat))}&lng=${encodeURIComponent(String(lng))}`;
  let res: Response;
  try {
    res = await doFetch(url, { method: 'GET', headers: { Authorization: `Bearer ${bearer}`, Accept: 'image/png' } });
  } catch {
    return { kind: 'error', reason: 'network' };
  }
  const state = (res.headers.get('X-SV-Stamp-Tile') ?? '').toUpperCase();
  if (res.status === 200 && /^image\/png/i.test(res.headers.get('Content-Type') ?? '')) {
    try {
      const blob = await res.blob();
      return { kind: 'png', blob, state: state || 'DRAWN', key: res.headers.get('X-SV-Stamp-Tile-Key') };
    } catch {
      return { kind: 'error', reason: 'unexpected' };
    }
  }
  if (res.status === 204) return { kind: 'none', state: state || 'OFF' };
  if (res.status === 404 || res.status === 410) return { kind: 'none', state: 'ABSENT' };
  if (res.status === 429) return { kind: 'error', reason: 'rate-limited' };
  if (res.status === 503) return { kind: 'error', reason: 'unavailable' };
  return { kind: 'error', reason: 'unexpected' };
}

// ── the cache: one request per tile for the whole viewfinder session ──────────────────────────

const CACHE_MAX = 64;
const cache = new Map<string, Promise<TileAnswer>>();

/**
 * The tile for `ref`, requested by its CENTRE and memoised by key — the thumbnail's tile and the mosaic's centre are
 * the same request; re-opening the sheet costs nothing; a walk inside one tile costs one request. A failed answer is
 * evicted so a later attempt may succeed.
 */
export function loadTile(ref: TileRef, deps: Partial<TileFetchDeps> = {}): Promise<TileAnswer> {
  const key = tileKey(ref);
  const hit = cache.get(key);
  if (hit) return hit;
  const { lat, lng } = tileCentre(ref);
  const p = fetchStampTile(lat, lng, deps);
  cache.set(key, p);
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  void p.then((a) => {
    if (a.kind === 'error') cache.delete(key);
  });
  return p;
}

export function resetTileCacheForTests(): void {
  cache.clear();
}
